import React from 'react';
import { Plan } from '@/types/plan';
import { motion } from 'framer-motion';
import FAQs from '../pricing/FAQs';
import PlanFeatures from './PlanFeatures';

interface PlansSectionProps {
  plans: Plan[];
  selectedObjective: string;
  onGetStarted: (planId: string) => void;
}

export const PlansSection: React.FC<PlansSectionProps> = ({ plans, selectedObjective, onGetStarted }) => {
  // Separate plans into basic and advanced
  const basicPlans = plans.filter(plan => !plan.isAdvanced);
  const advancedPlans = plans.filter(plan => plan.isAdvanced);

  return (
    <>
      <div className="flex flex-col space-y-8">
        {/* Row 1: Basic plans centered */}
        <div className="flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {basicPlans.map((plan) => (
              <div
                key={plan.id}
                className="bg-white rounded-2xl p-8 border-2 border-gray-200 w-full"
              >
                <div className="flex flex-col h-full">
                  <div className="text-center mb-4 min-h-[4rem] flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  </div>
                  <p className="text-center text-gray-600 mb-6">{plan.description}</p>
                  <div className="text-center mb-8">
                    <span className="text-5xl font-bold text-[#151b59]">${plan.basePrice}</span>
                    {plan.id === "plan-0" ? (
                      <div className="text-green-600 font-medium mt-1">No credit card required</div>
                    ) : (
                      <div className="text-gray-500 mt-2">one-time</div>
                    )}
                    <button
                      onClick={() => onGetStarted(plan.id)}
                      className="w-full bg-[#ee99ea] text-dark rounded-lg px-6 py-3 hover:bg-[#f0a5ec] transition-colors mt-6"
                    >
                      Start My Journey
                    </button>
                  </div>
                  <div className="flex-grow">
                    <PlanFeatures 
                      mainFeatures={plan.mainFeatures} 
                      features={plan.features} 
                    />
                  </div>
                  <button
                    onClick={() => window.location.href = `/example-report?plan=${plan.id}`}
                    className="w-full bg-[#52D6DD] text-dark rounded-lg px-6 py-3 hover:bg-[#52D6DD]/90 transition-colors mt-6"
                  >
                    View Example Report
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2: Advanced plans evenly distributed */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {advancedPlans.map((plan) => (
            <div
              key={plan.id}
              className="relative before:absolute before:inset-[-2px] before:rounded-2xl before:bg-gradient-to-r before:from-[#f9c55f] before:via-[#fce0aa] before:to-[#f9c55f] before:animate-gradient"
            >
              <div className="bg-white rounded-2xl p-8 relative z-20 h-full">
                <div className="flex flex-col h-full">
                  <div className="text-center mb-4 min-h-[4rem] flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  </div>
                  <p className="text-center text-gray-600 mb-6">{plan.description}</p>
                  <div className="text-center mb-8">
                    <span className="text-5xl font-bold text-[#151b59]">${plan.basePrice}</span>
                    {plan.id === "plan-0" ? (
                      <div className="text-green-600 font-medium mt-1">No credit card required</div>
                    ) : (
                      <div className="text-gray-500 mt-2">one-time</div>
                    )}
                    <button
                      onClick={() => onGetStarted(plan.id)}
                      className="w-full bg-[#ee99ea] text-dark rounded-lg px-6 py-3 hover:bg-[#f0a5ec] transition-colors mt-6"
                    >
                      Start My Journey
                    </button>
                  </div>
                  <div className="flex-grow">
                    <PlanFeatures 
                      mainFeatures={plan.mainFeatures} 
                      features={plan.features} 
                    />
                  </div>
                  <button
                    onClick={() => window.location.href = `/example-report?plan=${plan.id}`}
                    className="w-full bg-[#52D6DD] text-dark rounded-lg px-6 py-3 hover:bg-[#52D6DD]/90 transition-colors mt-6"
                  >
                    View Example Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <FAQs />
    </>
  );
};

export default PlansSection;